import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { getApiBaseUrl } from "@/lib/config";
import { clearPricingSnapshot } from "@/lib/pricingPrefetch";

const API = getApiBaseUrl();
const STANDARD_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const IDLE_WARNING_LEAD_MS = 1 * 60 * 1000;
const LAST_ACTIVITY_STORAGE_KEY = "voxara_last_activity_at";
const USER_SNAPSHOT_STORAGE_KEY = "voxara_user_snapshot";
const EXEMPT_MULTISESSION_MEMBERSHIPS = new Set(["enterprise", "business"]);
const ACTIVE_TAB_LOCK_KEY = "voxara_active_auth_tab_lock";
const USER_SESSION_EVENT_KEY = "voxara_user_session_event";
const TAB_HEARTBEAT_INTERVAL_MS = 4000;
const TAB_STALE_THRESHOLD_MS = 12000;
const SESSION_VALIDATION_INTERVAL_MS = 10000;
// A recording stops itself at four hours; the extra hour covers uploading what it captured.
const SESSION_HOLD_MAX_MS = 5 * 60 * 60 * 1000;

const readStoredUserSnapshot = () => {
  try {
    const raw = sessionStorage.getItem(USER_SNAPSHOT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const decodeJwtPayload = (accessToken) => {
  try {
    if (!accessToken) return null;
    const [, payload] = String(accessToken).split(".");
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const buildStoredSessionUser = (accessToken, storedSnapshot, storedIsAdmin = false) => {
  if (storedSnapshot && typeof storedSnapshot === "object") {
    return {
      ...storedSnapshot,
      is_admin: Boolean(storedSnapshot.is_admin || storedIsAdmin),
    };
  }

  const payload = decodeJwtPayload(accessToken);
  if (!payload?.sub) {
    return null;
  }

  const fallbackIsAdmin = Boolean(storedIsAdmin || payload.is_admin);
  return {
    id: payload.sub,
    username: fallbackIsAdmin ? "admin" : "user",
    email: null,
    created_at: new Date().toISOString(),
    membership: fallbackIsAdmin ? "enterprise" : "free",
    is_admin: fallbackIsAdmin,
  };
};

const createTabInstanceId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `voxara-tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const initialToken = sessionStorage.getItem("voxara_token");
  const initialStoredIsAdmin = sessionStorage.getItem("voxara_is_admin") === "true";
  const initialUserSnapshot = readStoredUserSnapshot();
  const [user, setUser] = useState(buildStoredSessionUser(initialToken, initialUserSnapshot, initialStoredIsAdmin));
  // Use sessionStorage so each tab/window has its own session
  const [token, setToken] = useState(initialToken);
  const [isAdmin, setIsAdmin] = useState(Boolean(initialStoredIsAdmin || initialUserSnapshot?.is_admin));
  const [loading, setLoading] = useState(true);
  const [forcedLogoutReason, setForcedLogoutReason] = useState(null);
  const [idleWarningVisible, setIdleWarningVisible] = useState(false);
  const idleTimerRef = useRef(null);
  const idleWarningTimerRef = useRef(null);
  const tabInstanceIdRef = useRef(createTabInstanceId());
  const tabHeartbeatRef = useRef(null);
  const authChannelRef = useRef(null);
  const idleStateRequestInFlightRef = useRef(false);

  // Reasons the page knows about that the server cannot: id -> absolute expiry.
  const sessionHoldsRef = useRef(new Map());

  // The oldest still-valid hold, or null. Doubles as "is anything held" and as the tiebreak when
  // two tabs both claim to be busy.
  const activeHoldSince = () => {
    const now = Date.now();
    let earliest = null;
    sessionHoldsRef.current.forEach((hold, id) => {
      if (hold.expiresAt <= now) {
        sessionHoldsRef.current.delete(id);
        return;
      }
      if (earliest === null || hold.startedAt < earliest) earliest = hold.startedAt;
    });
    return earliest;
  };

  const hasActiveSessionHold = () => activeHoldSince() !== null;

  // Keeps the session alive while something is happening that only this tab can see, such as a
  // recording in progress. Always bounded: a hold nobody released must not outlive its purpose.
  const holdSession = React.useCallback((reason, maxMs = SESSION_HOLD_MAX_MS) => {
    const id = `${reason || "hold"}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    const cap = Math.min(Math.max(Number(maxMs) || 0, 0), SESSION_HOLD_MAX_MS);
    const startedAt = Date.now();
    sessionHoldsRef.current.set(id, { startedAt, expiresAt: startedAt + cap });
    return () => sessionHoldsRef.current.delete(id);
  }, []);

  const isMultiSessionExempt = Boolean(
    isAdmin || EXEMPT_MULTISESSION_MEMBERSHIPS.has(String(user?.membership || "").toLowerCase())
  );
  const isIdleTimeoutExempt = Boolean(isAdmin);

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (idleWarningTimerRef.current) {
      window.clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }
  };

  const clearTabHeartbeat = () => {
    if (tabHeartbeatRef.current) {
      window.clearInterval(tabHeartbeatRef.current);
      tabHeartbeatRef.current = null;
    }
  };

  const closeAuthChannel = () => {
    if (authChannelRef.current) {
      authChannelRef.current.close();
      authChannelRef.current = null;
    }
  };

  const getSessionOwnershipKey = () => {
    if (!token) return null;
    const userId = String(user?.id || "anonymous");
    const tokenSuffix = String(token).slice(-24);
    return `${userId}:${tokenSuffix}`;
  };

  const getSessionFingerprint = () => {
    if (!token) return null;
    return String(token).slice(-24);
  };

  const readActiveTabLock = () => {
    try {
      const raw = localStorage.getItem(ACTIVE_TAB_LOCK_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const writeActiveTabLock = (sessionKey) => {
    const nextLock = {
      ownerId: tabInstanceIdRef.current,
      sessionKey,
      heartbeatAt: Date.now(),
      // Present only while this tab is doing something it must not be interrupted for.
      holdSince: activeHoldSince(),
    };
    localStorage.setItem(ACTIVE_TAB_LOCK_KEY, JSON.stringify(nextLock));
    return nextLock;
  };

  const isLockFresh = (lockValue) => {
    if (!lockValue?.heartbeatAt) return false;
    return Date.now() - Number(lockValue.heartbeatAt) < TAB_STALE_THRESHOLD_MS;
  };

  const readLatestUserSessionEvent = () => {
    try {
      const raw = localStorage.getItem(USER_SESSION_EVENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  };

  const publishUserSessionEvent = ({ userId, sessionFingerprint, isExempt }) => {
    if (!userId || !sessionFingerprint || isExempt) {
      return;
    }
    localStorage.setItem(
      USER_SESSION_EVENT_KEY,
      JSON.stringify({
        type: "voxara-user-session-updated",
        userId,
        sessionFingerprint,
        issuedAt: Date.now(),
      })
    );
  };

  const releaseActiveTabLock = () => {
    const existingLock = readActiveTabLock();
    if (existingLock?.ownerId === tabInstanceIdRef.current) {
      localStorage.removeItem(ACTIVE_TAB_LOCK_KEY);
    }
  };

  const redirectToLoginIfNeeded = (reason = "session-conflict") => {
    if (!["/login", "/signup"].includes(window.location.pathname)) {
      window.location.replace(`/login?reason=${reason}`);
    }
  };

  const forceLogoutAndRedirect = (reason) => {
    setForcedLogoutReason(reason);
    clearAuthSession({ preserveForcedReason: true });
    redirectToLoginIfNeeded(reason);
  };

  const storeLastActivity = (timestamp = Date.now()) => {
    sessionStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp));
  };

  const persistUserSnapshot = (userData, nextIsAdmin = false) => {
    try {
      if (!userData || typeof userData !== "object") {
        sessionStorage.removeItem(USER_SNAPSHOT_STORAGE_KEY);
        return null;
      }

      const snapshot = {
        ...userData,
        is_admin: Boolean(nextIsAdmin || userData.is_admin),
      };
      sessionStorage.setItem(USER_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
      return snapshot;
    } catch {
      return userData;
    }
  };

  const persistAuthSession = (accessToken, userData, nextIsAdmin = false) => {
    setForcedLogoutReason(null);
    sessionStorage.setItem("voxara_token", accessToken);
    sessionStorage.setItem("voxara_is_admin", nextIsAdmin ? "true" : "false");
    storeLastActivity();
    const sessionUser = persistUserSnapshot(userData, nextIsAdmin);
    publishUserSessionEvent({
      userId: sessionUser?.id,
      sessionFingerprint: String(accessToken).slice(-24),
      isExempt:
        Boolean(nextIsAdmin) || EXEMPT_MULTISESSION_MEMBERSHIPS.has(String(sessionUser?.membership || "").toLowerCase()),
    });
    setToken(accessToken);
    setUser(sessionUser || userData);
    setIsAdmin(Boolean(nextIsAdmin));
  };

  const refreshUser = async () => {
    if (!token) return null;

    const response = await axios.get(`${API}/auth/me`);
    const sessionUser = persistUserSnapshot(response.data, response.data.is_admin || false);
    setUser(sessionUser || response.data);
    setIsAdmin(response.data.is_admin || false);
    sessionStorage.setItem("voxara_is_admin", response.data.is_admin ? "true" : "false");
    return sessionUser || response.data;
  };

  const clearAuthSession = ({ preserveForcedReason = false } = {}) => {
    clearIdleTimer();
    clearTabHeartbeat();
    closeAuthChannel();
    releaseActiveTabLock();
    setIdleWarningVisible(false);
    sessionStorage.removeItem("voxara_token");
    sessionStorage.removeItem("voxara_is_admin");
    sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
    sessionStorage.removeItem(USER_SNAPSHOT_STORAGE_KEY);
    clearPricingSnapshot();
    setToken(null);
    setUser(null);
    setIsAdmin(false);
    if (!preserveForcedReason) {
      setForcedLogoutReason(null);
    }
  };

  // Set axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const authEntryPath = ["/login", "/signup"].includes(window.location.pathname);
      const storedIsAdmin = sessionStorage.getItem("voxara_is_admin") === "true";
      const storedSnapshot = readStoredUserSnapshot();

      if (token && authEntryPath && !storedIsAdmin) {
        clearAuthSession();
        setLoading(false);
        return;
      }

      if (token) {
        try {
          await refreshUser();
        } catch (error) {
          console.error("Auth check failed:", error);
          const status = error?.response?.status || 0;
          const detail = String(error?.response?.data?.detail || error?.message || "").toLowerCase();
          const shouldInvalidateSession =
            status === 401 ||
            status === 403 ||
            /(session invalidated|newer login|token expired|not authenticated|could not validate credentials|invalid token|unauthorized|forbidden)/i.test(detail);

          if (shouldInvalidateSession) {
            clearAuthSession();
          } else {
            const fallbackUser = buildStoredSessionUser(token, storedSnapshot, storedIsAdmin);
            if (fallbackUser) {
              setUser(fallbackUser);
              setIsAdmin(Boolean(fallbackUser.is_admin || storedIsAdmin));
            }
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  useEffect(() => {
    if (loading || !token || isIdleTimeoutExempt) {
      clearIdleTimer();
      setIdleWarningVisible(false);
      return undefined;
    }

    const logoutForInactivity = () => {
      forceLogoutAndRedirect("idle");
    };

    const getLastActivity = () => {
      const storedValue = Number(sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY) || Date.now());
      return Number.isFinite(storedValue) ? storedValue : Date.now();
    };

    const refreshIdleWindow = () => {
      storeLastActivity();
      setIdleWarningVisible(false);
    };

    const userHasActiveProcessing = async () => {
      // Checked before the network call: a recording in progress is invisible to the server, and
      // asking it would only produce a confident "nothing active" while a hearing is being taped.
      if (hasActiveSessionHold()) {
        return true;
      }
      if (idleStateRequestInFlightRef.current) {
        return false;
      }
      idleStateRequestInFlightRef.current = true;
      try {
        const response = await axios.get(`${API}/auth/session-idle-state`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        return Boolean(response?.data?.has_active_processing);
      } catch (error) {
        const status = error?.response?.status || 0;
        const detail = String(error?.response?.data?.detail || error?.message || "").toLowerCase();
        if (
          status === 401 ||
          status === 403 ||
          /(session invalidated|newer login|token expired|not authenticated|could not validate credentials|invalid token|unauthorized|forbidden)/i.test(detail)
        ) {
          forceLogoutAndRedirect("session-conflict");
        }
        return false;
      } finally {
        idleStateRequestInFlightRef.current = false;
      }
    };

    const scheduleIdleTimeout = () => {
      const lastActivity = getLastActivity();
      const elapsed = Date.now() - lastActivity;
      const warningThresholdMs = STANDARD_IDLE_TIMEOUT_MS - IDLE_WARNING_LEAD_MS;

      clearIdleTimer();

      if (elapsed >= STANDARD_IDLE_TIMEOUT_MS) {
        idleTimerRef.current = window.setTimeout(async () => {
          const shouldPauseTimeout = await userHasActiveProcessing();
          if (shouldPauseTimeout) {
            refreshIdleWindow();
            scheduleIdleTimeout();
            return;
          }
          logoutForInactivity();
        }, 0);
        return;
      }

      if (elapsed >= warningThresholdMs) {
        idleWarningTimerRef.current = window.setTimeout(async () => {
          const shouldPauseTimeout = await userHasActiveProcessing();
          if (shouldPauseTimeout) {
            refreshIdleWindow();
            scheduleIdleTimeout();
            return;
          }
          setIdleWarningVisible(true);
        }, 0);
      } else {
        idleWarningTimerRef.current = window.setTimeout(async () => {
          const shouldPauseTimeout = await userHasActiveProcessing();
          if (shouldPauseTimeout) {
            refreshIdleWindow();
            scheduleIdleTimeout();
            return;
          }
          setIdleWarningVisible(true);
        }, warningThresholdMs - elapsed);
      }

      idleTimerRef.current = window.setTimeout(async () => {
        const shouldPauseTimeout = await userHasActiveProcessing();
        if (shouldPauseTimeout) {
          refreshIdleWindow();
          scheduleIdleTimeout();
          return;
        }
        logoutForInactivity();
      }, STANDARD_IDLE_TIMEOUT_MS - elapsed);
    };

    const markActivity = () => {
      refreshIdleWindow();
      scheduleIdleTimeout();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleIdleTimeout();
      }
    };

    if (!sessionStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) {
      storeLastActivity();
    }
    scheduleIdleTimeout();

    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, markActivity));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearIdleTimer();
    };
  }, [token, loading, isIdleTimeoutExempt]);

  useEffect(() => {
    if (loading || !token || isMultiSessionExempt) {
      clearTabHeartbeat();
      closeAuthChannel();
      releaseActiveTabLock();
      return undefined;
    }

    const sessionKey = getSessionOwnershipKey();
    if (!sessionKey) {
      return undefined;
    }

    // This tab outranks the other when it is busy and the other either is not, or started being
    // busy later. Both tabs compute it from the same two numbers, so exactly one of them wins.
    const outranks = (theirHoldSince) => {
      const mine = activeHoldSince();
      if (mine === null) return false;
      if (theirHoldSince === null || theirHoldSince === undefined) return true;
      return mine < Number(theirHoldSince);
    };

    const yieldTo = (theirHoldSince) => {
      forceLogoutAndRedirect(
        theirHoldSince === null || theirHoldSince === undefined
          ? "session-conflict"
          : "recording-elsewhere",
      );
    };

    const claimOwnership = () => {
      const currentLock = readActiveTabLock();
      if (
        currentLock?.sessionKey === sessionKey &&
        currentLock.ownerId !== tabInstanceIdRef.current &&
        isLockFresh(currentLock)
      ) {
        authChannelRef.current?.postMessage({
          type: "voxara-session-claimed",
          sessionKey,
          ownerId: tabInstanceIdRef.current,
          holdSince: activeHoldSince(),
        });
      }

      const nextLock = writeActiveTabLock(sessionKey);
      authChannelRef.current?.postMessage({
        type: "voxara-session-claimed",
        sessionKey,
        ownerId: nextLock.ownerId,
        holdSince: nextLock.holdSince,
      });
    };

    const verifyOrRefreshOwnership = () => {
      const existingLock = readActiveTabLock();

      if (!existingLock || existingLock.sessionKey !== sessionKey || !isLockFresh(existingLock)) {
        claimOwnership();
        return;
      }

      if (existingLock.ownerId !== tabInstanceIdRef.current) {
        // A recording in progress takes the lock back rather than dying for a tab someone just
        // opened. With no recording, newest still wins.
        if (outranks(existingLock.holdSince)) {
          claimOwnership();
          return;
        }
        yieldTo(existingLock.holdSince);
        return;
      }

      writeActiveTabLock(sessionKey);
    };

    authChannelRef.current = new BroadcastChannel("voxara-auth-session");

    const handleChannelMessage = (event) => {
      const message = event?.data;
      if (!message || message.sessionKey !== sessionKey) return;
      if (message.ownerId === tabInstanceIdRef.current) return;
      if (message.type === "voxara-session-claimed") {
        if (outranks(message.holdSince)) {
          claimOwnership();
          return;
        }
        yieldTo(message.holdSince);
      }
    };

    const handleStorageEvent = (event) => {
      if (event.key !== ACTIVE_TAB_LOCK_KEY || !event.newValue) return;
      try {
        const nextLock = JSON.parse(event.newValue);
        if (!nextLock || nextLock.sessionKey !== sessionKey) return;
        if (nextLock.ownerId !== tabInstanceIdRef.current && isLockFresh(nextLock)) {
          if (outranks(nextLock.holdSince)) {
            claimOwnership();
            return;
          }
          yieldTo(nextLock.holdSince);
        }
      } catch {
        // noop
      }
    };

    const handleBeforeUnload = () => {
      releaseActiveTabLock();
    };

    authChannelRef.current.addEventListener("message", handleChannelMessage);
    window.addEventListener("storage", handleStorageEvent);
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handleWindowFocus = () => {
      verifyOrRefreshOwnership();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        verifyOrRefreshOwnership();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    claimOwnership();
    tabHeartbeatRef.current = window.setInterval(() => {
      verifyOrRefreshOwnership();
    }, TAB_HEARTBEAT_INTERVAL_MS);

    return () => {
      authChannelRef.current?.removeEventListener("message", handleChannelMessage);
      window.removeEventListener("storage", handleStorageEvent);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTabHeartbeat();
      closeAuthChannel();
      releaseActiveTabLock();
    };
  }, [token, loading, isMultiSessionExempt, user?.id]);

  useEffect(() => {
    if (loading || !token || !user?.id || isMultiSessionExempt) {
      return undefined;
    }

    const currentFingerprint = getSessionFingerprint();
    if (!currentFingerprint) {
      return undefined;
    }

    const shouldForceLogoutForSessionEvent = (eventPayload) => {
      if (!eventPayload || eventPayload.type !== "voxara-user-session-updated") {
        return false;
      }
      if (eventPayload.userId !== user.id) {
        return false;
      }
      return eventPayload.sessionFingerprint !== currentFingerprint;
    };

    const validateLatestSessionEvent = () => {
      const latestEvent = readLatestUserSessionEvent();
      if (shouldForceLogoutForSessionEvent(latestEvent)) {
        forceLogoutAndRedirect("session-conflict");
      }
    };

    const handleStorageEvent = (event) => {
      if (event.key !== USER_SESSION_EVENT_KEY || !event.newValue) {
        return;
      }
      try {
        const nextEvent = JSON.parse(event.newValue);
        if (shouldForceLogoutForSessionEvent(nextEvent)) {
          forceLogoutAndRedirect("session-conflict");
        }
      } catch {
        // noop
      }
    };

    const handleWindowFocus = () => {
      validateLatestSessionEvent();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        validateLatestSessionEvent();
      }
    };

    validateLatestSessionEvent();
    window.addEventListener("storage", handleStorageEvent);
    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, loading, isMultiSessionExempt, user?.id]);

  useEffect(() => {
    if (loading || !token || isMultiSessionExempt) {
      return undefined;
    }

    let intervalId = null;
    let validationInFlight = false;
    let cancelled = false;

    const shouldSkipTransientSessionValidation = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return true;
      }

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return true;
      }

      return false;
    };

    const validateActiveSession = async () => {
      if (validationInFlight || cancelled) {
        return;
      }

      if (shouldSkipTransientSessionValidation()) {
        return;
      }

      validationInFlight = true;
      try {
        await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
      } catch (error) {
        const status = error?.response?.status;
        const detail = String(error?.response?.data?.detail || "").toLowerCase();
        if (
          status === 401 ||
          (status === 403 && /(session invalidated|newer login|not authenticated|could not validate credentials|invalid token|unauthorized|forbidden)/i.test(detail))
        ) {
          forceLogoutAndRedirect("session-conflict");
        }
      } finally {
        validationInFlight = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        window.setTimeout(() => {
          validateActiveSession();
        }, 1200);
      }
    };

    intervalId = window.setInterval(validateActiveSession, SESSION_VALIDATION_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, loading, isMultiSessionExempt]);

  useEffect(() => {
    const handleAuthInvalidation = (event) => {
      const reason = event?.detail?.reason || "session-conflict";
      forceLogoutAndRedirect(reason);
    };

    window.addEventListener("voxara-auth-invalidated", handleAuthInvalidation);
    return () => {
      window.removeEventListener("voxara-auth-invalidated", handleAuthInvalidation);
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, is_admin, mfa_required, mfa_token, message } = response.data;

    if (mfa_required) {
      clearAuthSession();
      return {
        mfaRequired: true,
        mfaToken: mfa_token,
        email,
        isAdmin: is_admin || false,
        message,
      };
    }
    
    persistAuthSession(access_token, userData, is_admin || false);
    
    return { user: userData, isAdmin: is_admin };
  };

  const verifyMfa = async (mfaToken, pin) => {
    const response = await axios.post(`${API}/auth/verify-mfa`, {
      mfa_token: mfaToken,
      pin,
    });
    const { access_token, user: userData, is_admin } = response.data;

    persistAuthSession(access_token, userData, is_admin || false);

    return { user: userData, isAdmin: is_admin };
  };

  const resendMfa = async (mfaToken) => {
    const response = await axios.post(`${API}/auth/resend-mfa`, null, {
      params: { mfa_token: mfaToken },
    });
    return response.data;
  };

  const register = async (email, password, options = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      first_name: options.firstName,
      last_name: options.lastName,
      phone_country_iso2: options.phoneCountryIso2,
      phone_country_code: options.phoneCountryCode,
      phone_number: options.phoneNumber,
      terms_accepted: Boolean(options.termsAccepted),
      terms_version: options.termsVersion,
    });
    const { access_token, user: userData, is_admin, mfa_required, mfa_token, message } = response.data;

    if (mfa_required) {
      clearAuthSession();
      return {
        mfaRequired: true,
        mfaToken: mfa_token,
        email,
        isAdmin: is_admin || false,
        message,
      };
    }
    
    persistAuthSession(access_token, userData, false);
    
    return userData;
  };

  const logout = () => {
    clearAuthSession();
  };

  const requestPasswordReset = async (email) => {
    const response = await axios.post(`${API}/auth/request-reset`, { email });
    return response.data;
  };

  const resetPassword = async (email, code, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      email,
      code,
      new_password: newPassword,
    });
    return response.data;
  };

  const value = {
    user,
    token,
    isAdmin,
    loading,
    holdSession,
    login,
    register,
    logout,
    refreshUser,
    verifyMfa,
    resendMfa,
    requestPasswordReset,
    resetPassword,
  };

  if (forcedLogoutReason) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="forced-logout-screen">
        <div className="max-w-md text-center space-y-3">
          <div className="text-lg font-semibold text-foreground">
            {forcedLogoutReason === "recording-elsewhere"
              ? "Hay una grabación en curso"
              : forcedLogoutReason === "session-conflict"
                ? "Sesión cerrada por seguridad"
                : "Sesión expirada"}
          </div>
          <p className="text-sm text-muted-foreground">
            {forcedLogoutReason === "recording-elsewhere"
              ? "Tu cuenta está grabando en otra pestaña y la dejamos seguir para no perder el audio. Vuelve a esa pestaña, o detén la grabación antes de abrir Voxara aquí."
              : forcedLogoutReason === "session-conflict"
                ? "Esta cuenta se abrió en otra pestaña. Redirigiendo al inicio de sesión..."
                : "Tu sesión expiró por inactividad. Redirigiendo al inicio de sesión..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {idleWarningVisible && !forcedLogoutReason ? (
        <div className="fixed right-4 top-4 z-[120] max-w-sm rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-left shadow-lg" data-testid="session-idle-warning-banner">
          <div className="text-sm font-semibold text-amber-900" data-testid="session-idle-warning-title">
            Sesión por expirar
          </div>
          <p className="mt-1 text-sm text-amber-800" data-testid="session-idle-warning-message">
            Tu sesión se cerrará en 1 minuto por inactividad. Si tienes un video en cola o procesándose, el contador se pausa automáticamente.
          </p>
        </div>
      ) : null}
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
